import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowLeft, X, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, naira } from "@/lib/api";

export default function Investments() {
  const { setCharacter } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [trade, setTrade] = useState(null); // {asset, side}
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/investments"); setData(r.data); }
    catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const open = (asset, side) => { setTrade({ asset, side }); setQty(1); };

  const confirm = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/investments/${trade.side}`, { asset_id: trade.asset.id, qty: Number(qty) });
      setCharacter(res.character);
      toast.success(trade.side === "buy" ? `Bought ${qty} × ${trade.asset.id} for ${naira(res.spent)}` : `Sold ${qty} × ${trade.asset.id} for ${naira(res.proceeds)}`);
      setTrade(null); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!data) return null;
  const stocks = data.assets.filter((a) => a.type === "stock");
  const land = data.assets.filter((a) => a.type === "land");

  const Row = (a) => {
    const Icon = Icons[a.icon.split("-").map((s) => s[0].toUpperCase() + s.slice(1)).join("")] || TrendingUp;
    return (
      <div key={a.id} data-testid={`asset-${a.id}`} className="bg-white border border-slate-200 rounded-xl p-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#FF66B2]/10 flex items-center justify-center"><Icon size={18} className="text-[#FF66B2]" /></div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate">{a.name}</div>
            <div className="label-xs text-slate-500">{a.id} · {a.type === "stock" ? "per unit" : "per plot"} {naira(a.price)}</div>
          </div>
          {a.qty > 0 && (
            <div className="text-right">
              <div className="label-xs text-slate-500">You own {a.qty}</div>
              <div className="text-[#16A34A] font-display text-sm">{naira(a.value)}</div>
            </div>
          )}
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <button data-testid={`buy-${a.id}`} onClick={() => open(a, "buy")} className="bg-[#16A34A] text-white font-bold py-2 rounded-lg label-xs">Buy</button>
          <button data-testid={`sell-${a.id}`} disabled={a.qty <= 0} onClick={() => open(a, "sell")} className="bg-slate-100 border border-[#FF0055]/40 text-[#FF0055] font-bold py-2 rounded-lg label-xs disabled:opacity-30">Sell</button>
        </div>
      </div>
    );
  };

  return (
    <div className="px-5 pt-6" data-testid="investments-page">
      <div className="flex items-center gap-3">
        <button data-testid="inv-back" onClick={() => nav("/city")} className="p-2 rounded-full bg-slate-100"><ArrowLeft size={18} /></button>
        <div>
          <h1 className="font-display text-3xl">Invest <span className="text-[#FF66B2]">& Grow</span></h1>
          <p className="label-xs text-slate-500">NGX stocks & land — prices move live</p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2">
        <div className="bg-white border border-slate-200 rounded-xl p-3">
          <div className="label-xs text-slate-500">Wallet</div>
          <div className="text-[#16A34A] font-display" data-testid="inv-wallet">{naira(data.wallet)}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-3">
          <div className="label-xs text-slate-500">Portfolio</div>
          <div className="text-[#FF66B2] font-display" data-testid="inv-portfolio">{naira(data.portfolio)}</div>
        </div>
      </div>

      <h2 className="mt-5 label-xs text-slate-500">NGX Stocks</h2>
      <div className="mt-2 space-y-2">{stocks.map(Row)}</div>
      <h2 className="mt-5 label-xs text-slate-500">Land Plots</h2>
      <div className="mt-2 space-y-2 mb-4">{land.map(Row)}</div>

      <AnimatePresence>
        {trade && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-40 bg-black/60" onClick={() => setTrade(null)} />
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              className="fixed left-1/2 -translate-x-1/2 bottom-28 z-50 w-[88%] max-w-sm bg-white border border-slate-200 rounded-2xl p-5" data-testid="trade-modal">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-display text-lg capitalize">{trade.side} {trade.asset.id}</h3>
                <button data-testid="trade-close" onClick={() => setTrade(null)} className="p-1.5 rounded-full bg-slate-100"><X size={16} /></button>
              </div>
              <p className="text-sm text-slate-500">{trade.asset.name}</p>
              <p className="label-xs text-slate-500 mt-1">Price {naira(trade.asset.price)} {trade.side === "sell" ? `· you own ${trade.asset.qty}` : ""}</p>
              <input data-testid="trade-qty" type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)}
                className="mt-3 w-full bg-slate-100 border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:border-[#FF66B2] outline-none" />
              <div className="mt-2 flex items-center justify-between label-xs">
                <span className="text-slate-500">{trade.side === "buy" ? "Total cost" : "You receive"}</span>
                <span className="text-[#16A34A] font-display">{naira(Math.round((Number(qty) || 0) * trade.asset.price))}</span>
              </div>
              <button data-testid="trade-confirm" disabled={busy} onClick={confirm}
                className={`mt-4 w-full py-3 rounded-xl font-bold text-slate-900 disabled:opacity-50 ${trade.side === "buy" ? "bg-[#16A34A]" : "bg-[#FF0055] text-white"}`}>
                Confirm {trade.side}
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
